import React, { useCallback, useMemo, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { DocumentTemplatesProvider } from '~/components/AiChat/hooks/useActionDocumentTemplate';
import type { AiAction } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import { useConductorHubNotifications } from '~/components/ClaimPage/CliveApp/ConductorHub/hooks/useConductorHubNotifications';
import { useConductorStatusObservation } from '~/components/ClaimPage/CliveApp/hooks/useConductorStatusObservation';
import useClaimStageConfigurations from '~/components/ClaimStages/hooks/useClaimStageConfigurations';
import { ClaimReferencesProvider } from '~/components/Clive/ClaimReferences/ClaimReferencesProvider';
import { FeedbackSnackbar } from '~/components/core/Molecules/Snackbar';
import LoadingIndicator from '~/components/LoadingIndicator';
import useDataFetcher from '~/components/useDataFetcher';
import { useLoadingSpan } from '~/observability/useLoadingSpan';
import type {
  ClaimActionsListResponse,
  ClaimConductorAsksResponse,
  ClaimConductorStageHistoryResponse,
  ClaimConductorStageHistoryResponseClaimStageHistoryEntry as StageHistoryEntry,
  ClaimConductorStagesResponse,
} from '~/server_shared/openapi/data-contracts';

import { PendingActionsProvider } from './ActionGestures/PendingActions';
import { buildActivityRows } from './ActivityPanel/activityRows';
import type { PaneArrival } from './ActivityPanel/types';
import { useHandlerMessages } from './hooks/useHandlerMessages';
import { useRefetchForNewAskEntries } from './hooks/useRefetchForNewAskEntries';
import { useRefetchForUnreadActions } from './hooks/useRefetchForUnreadActions';
import { useRefreshClaimOnStageChange } from './hooks/useRefreshClaimOnStageChange';
import { useSelectLogEntry } from './hooks/useSelectLogEntry';
import { useSupersededActionVersions } from './hooks/useSupersededActionVersions';
import { buildStageChapters } from './LogPanel/StagesRail/utils';
import ActivityPanel from './ActivityPanel';
import ConductorHubSkeleton from './ConductorHubSkeleton';
import { LOG_EVENT_LIMIT, LOG_PANE_DEFAULT_SIZE } from './constants';
import LogPanel from './LogPanel';
import StageBar from './StageBar';
import type { ConductorAsk } from './types';

/** Stable empty arrays until the requests land, so the panes' memos hold. */
const NO_ACTIONS: AiAction[] = [];
const NO_ASKS: ConductorAsk[] = [];
const NO_STAGE_CHANGES: StageHistoryEntry[] = [];

/**
 * The claim's actions.
 *
 * Not through `CliveInsights`' `ActionsDataProvider`: it injects demo actions and filters by
 * per-type flags. `additional_data=false` must be explicit — the route defaults it to truthy and
 * would run the demo-data queries. No error dialog: the tab shows its own error state.
 */
const useClaimActions = (claimId: number) =>
  useDataFetcher<ClaimActionsListResponse>(`/api/v1/claims/${claimId}/actions?additional_data=false`, null, {
    shouldOpenErrorDialog: false,
  });

/**
 * The asks Clive raised on the claim, open and closed. Waited on like the stages, and like them its
 * failure is not the tab's: an ask entry whose ask was not read is still drawn from what the entry
 * says, so a failed read leaves those entries unjoined rather than blanking the panes over them.
 */
const useClaimAsks = (claimId: number) =>
  useDataFetcher<ClaimConductorAsksResponse>(`/api/v1/claims/${claimId}/conductor/asks`, null, {
    shouldOpenErrorDialog: false,
  });

/**
 * The stages the conductor is configured for, for the stage bar. Waited on like the sources so the
 * bar cannot land above panes already on screen and push them down, but its failure is not the
 * tab's: the bar is chrome, so a failed read leaves it undrawn rather than erroring the tab.
 */
const useConductorStages = (claimId: number) =>
  useDataFetcher<ClaimConductorStagesResponse>(`/api/v1/claims/${claimId}/conductor/stages`, null, {
    shouldOpenErrorDialog: false,
  });

/**
 * The claim's stage changes, for the log's stages rail and the stage bar's return mark. Waited on like the
 * stages, so the rail cannot land beside a log already drawn and narrow it, and like them its failure is not
 * the tab's: a failed read leaves the rail undrawn and the bar unmarked.
 */
const useConductorStageHistory = (claimId: number) =>
  useDataFetcher<ClaimConductorStageHistoryResponse>(`/api/v1/claims/${claimId}/conductor/stages/history`, null, {
    shouldOpenErrorDialog: false,
  });

/**
 * The `conductor_hub` claim tab, titled "Automation First": the claim's stage bar across the top,
 * then the log panel on the left at 68% and the activity panel on the right, resizable from the
 * handle between them. `PanelGroup` applies `height: 100%` / `width: 100%` itself, so it sits in a
 * `min-h-0 flex-1` box that is the height left under the bar. The log is the main panel and keeps
 * 30% of the width whatever the handle is dragged to — collapsing it entirely hides the thing the
 * tab is for. Everything that fills the panes lands under this folder.
 *
 * Reads the claim's sources once — one subscription, one actions request, one asks request — and
 * hands both panes the same rows, so a card cannot say one thing in the log and another in the
 * activity panel. The log alone takes the conversation with Clive: its status and the handler's
 * messages.
 */
const ConductorHub: React.FC = () => {
  const { claim } = useClaim({ claimRequired: true });

  // The log keeps what it has loaded: an entry the live window evicts is still the claim's history.
  const {
    notifications,
    isLoading: isStreamLoading,
    error: streamError,
    hasEarlier,
    loadEarlier,
    loadSince,
  } = useConductorHubNotifications({
    claimId: claim.id,
    eventLimit: LOG_EVENT_LIMIT,
    keepHistory: true,
  });
  const { data, isLoading: areActionsLoading, isError: isActionsError, reloadData } = useClaimActions(claim.id);
  const { data: asksData, isLoading: areAsksLoading, reloadData: reloadAsks } = useClaimAsks(claim.id);
  const { data: conductorStages, isLoading: areStagesLoading } = useConductorStages(claim.id);
  const conductorStageKeys = conductorStages?.stage_keys;
  const {
    data: stageHistoryData,
    isLoading: isStageHistoryLoading,
    reloadData: reloadStageHistory,
  } = useConductorStageHistory(claim.id);
  const stageHistory = stageHistoryData?.history ?? NO_STAGE_CHANGES;
  const { claimStageConfigurations } = useClaimStageConfigurations();
  // Named as the stage bar names the stages, and read for which way each move went against the bar's flow.
  // Memoized: the rail reads the log's layout again whenever its chapters change.
  const stageChapters = useMemo(
    () => buildStageChapters(stageHistory, conductorStageKeys ?? [], claimStageConfigurations),
    [stageHistory, conductorStageKeys, claimStageConfigurations]
  );
  const [paneArrival, setPaneArrival] = useState<PaneArrival>();

  // `reloadData` skips the loading state, so the panes (and an open dialog) stay mounted.
  // Memoized: `useActionCard` depends on it.
  const handleActionUpdate = useCallback(async (): Promise<void> => {
    await reloadData();
  }, [reloadData]);

  // Memoized: the log's memoized entries take it, and a new function each render would redraw them all.
  const handleAskUpdate = useCallback(async (): Promise<void> => {
    await reloadAsks();
  }, [reloadAsks]);

  // A new object on every click, even for the card the panel already shows: the panel rings and
  // scrolls on each arrival, so following the same entry again still brings its card back into view.
  // Memoized, as `handleAskUpdate` is: the log's memoized entries take it.
  const handleSelectActivityCard = useCallback((key: string): void => {
    setPaneArrival({ key });
  }, []);

  // Same rows, described more narrowly by `AiAction` (the `task_data` union, `Date` timestamps).
  const actions = (data?.actions as AiAction[] | undefined) ?? NO_ACTIONS;
  const asks = asksData?.items ?? NO_ASKS;

  // Firestore's initial snapshot arrives newest-first and `LogPanel` renders in the order given.
  // ISO-8601 timestamps sort lexicographically.
  const orderedNotifications = useMemo(
    () => [...notifications].sort((first, second) => first.timestamp.localeCompare(second.timestamp)),
    [notifications]
  );

  // `reloadData` and not `handleActionUpdate`: the hook reads the response to tell a failed read.
  useRefetchForUnreadActions(orderedNotifications, actions, Boolean(data) && !isStreamLoading, reloadData);
  // Settled rather than landed: a failed first read keeps the tab up, so its entries must still ask.
  useRefetchForNewAskEntries(orderedNotifications, !areAsksLoading && !isStreamLoading, reloadAsks);
  // The stream is what brings a stage change's row in (above); the row is what moves the bar.
  useRefreshClaimOnStageChange(actions, Boolean(data), reloadStageHistory);
  const actionVersions = useSupersededActionVersions(claim.id, orderedNotifications, actions);

  // Whoever drives the claim: the log's cursor, and what it shows once a run ends, follow a run either way.
  const status = useConductorStatusObservation({ enabled: true });
  // Waited on like the asks, so the queue cannot appear under a log already drawn. Its failure is not the
  // tab's: the queue stays as it was, empty if the first read failed, and the composer still sends.
  const {
    queue,
    isLoading: areMessagesLoading,
    send,
  } = useHandlerMessages(claim.id, orderedNotifications, !isStreamLoading, status);

  const rows = useMemo(
    () => buildActivityRows(actions, asks, orderedNotifications),
    [actions, asks, orderedNotifications]
  );
  const {
    selectedEventId,
    selectLogEntry,
    deselectLogEntry,
    selectChapter,
    chapterEntryIdInLog,
    pendingChapterKey,
    notice,
    closeNotice,
  } = useSelectLogEntry(rows, orderedNotifications, hasEarlier, loadSince);

  // Either source failing is an error: an empty activity panel after a failed fetch would claim
  // nothing is waiting. Only a failed first read, though: a refetch that fails keeps the rows it
  // had (`FETCH_FAILURE` leaves `data` alone), and unmounting the panes over it would tear down
  // an open dialog straight after a gesture that succeeded.
  const isLoading =
    isStreamLoading ||
    areActionsLoading ||
    areAsksLoading ||
    areStagesLoading ||
    isStageHistoryLoading ||
    areMessagesLoading;
  const isError = Boolean(streamError) || (isActionsError && !data);

  // `LoadingSwitch`, unrolled: its loading slot centres the component in a box with no height, where
  // the skeleton's `h-full` cannot resolve. The skeleton takes the tab's own box instead, and the
  // loading span and the error state stay as `LoadingSwitch` draws them.
  useLoadingSpan({ isLoading, isError, loadingSpanName: 'components/ClaimPage/CliveApp/ConductorHub' });

  if (isError) return <LoadingIndicator isError />;
  if (isLoading) return <ConductorHubSkeleton conductorStageKeys={conductorStageKeys} />;

  return (
    <PendingActionsProvider>
      {/* The cards in both panes, and the dialogs opened from them, share the claim's template lists. */}
      <DocumentTemplatesProvider>
        {/* Both panes, and the dialogs opened from their cards, resolve Clive's citations from one cache. */}
        <ClaimReferencesProvider claimId={claim.id}>
          <div className="flex h-full flex-col rounded bg-white py-16">
            <StageBar conductorStageKeys={conductorStageKeys} stageHistory={stageHistory} />
            <div className="min-h-0 flex-1">
              <PanelGroup direction="horizontal" id="conductor-hub">
                <Panel id="log-pane" defaultSize={LOG_PANE_DEFAULT_SIZE} minSize={30}>
                  <LogPanel
                    notifications={orderedNotifications}
                    actions={actions}
                    actionVersions={actionVersions}
                    asks={asks}
                    onActionUpdate={handleActionUpdate}
                    onAskUpdate={handleAskUpdate}
                    selectedEventId={selectedEventId}
                    onDeselect={deselectLogEntry}
                    hasEarlier={hasEarlier}
                    onLoadEarlier={loadEarlier}
                    onSelectActivityCard={handleSelectActivityCard}
                    conversation={{ status, queue, send }}
                    stagesRail={{
                      chapters: stageChapters,
                      onSelectChapter: selectChapter,
                      entryIdOf: chapterEntryIdInLog,
                      pendingChapterKey,
                    }}
                  />
                </Panel>
                <PanelResizeHandle id="resize-handle" className="mx-12 w-2 rounded-lg bg-slate-500" />
                <Panel id="activity-panel" minSize={100 - LOG_PANE_DEFAULT_SIZE}>
                  <ActivityPanel
                    rows={rows}
                    onActionUpdate={handleActionUpdate}
                    onAskUpdate={handleAskUpdate}
                    onSelectLogEntry={selectLogEntry}
                    arrival={paneArrival}
                  />
                </Panel>
              </PanelGroup>
            </div>
          </div>
        </ClaimReferencesProvider>
        <FeedbackSnackbar state={notice} onClose={closeNotice} />
      </DocumentTemplatesProvider>
    </PendingActionsProvider>
  );
};

export default ConductorHub;
